import Component from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';

/**
 * A custom element that renders a slideshow.
 */
export default class Slideshow extends Component {
  #scroll = null;
  #intersectionObserver = null;
  #resizeObserver = null;
  #autoplayTimer = undefined;
  #current = 0;
  #isInitializing = false;
  #isHibernating = false;

  constructor() {
    super();
  }

  get id() {
    return this.getAttribute('id');
  }

  get infinite() {
    return this.hasAttribute('infinite');
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  get current() { return this.#current; }
  set current(val) { this.#current = val; }

  connectedCallback() {
    super.connectedCallback();
    this.#current = this.#getInitialSlideIndex();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.initLazyInitialization();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.hibernate();
  }

  initLazyInitialization() {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          void this.#init();
        } else {
          this.#destroyScroller();
        }
      });
    }, {
      rootMargin: '100px'
    });

    this.#intersectionObserver.observe(this);
  }

  hibernate() {
    this.#isHibernating = true;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    this.suspend();
    this.#destroyScroller();
  }

  wakeUp() {
    this.#isHibernating = false;
    this.initLazyInitialization();
    void this.#init();
    this.resume();
  }

  async #init() {
    if (this.#isInitializing || this.#scroll || this.#isHibernating) return;
    this.#isInitializing = true;

    try {
      const { scroller } = this.refs;
      if (!scroller) return;

      this.#scroll = new Scroller(scroller, {
        onScroll: this.#handleScroll.bind(this),
        onTouchStart: this.#handleTouchStart.bind(this),
        onTransitionEnd: this.#onTransitionEnd.bind(this),
        onMouseDown: this.#handleMouseDown.bind(this)
      });

      this.#resizeObserver = new ResizeObserver(debounce(() => {
        this.#ensureCurrentSlide();
        this.#updateControlsVisibility();
        this.#updateVisibleSlides();
      }, 100));

      this.#resizeObserver.observe(scroller);

      this.#ensureCurrentSlide();
      const initialSlide = this.slides[this.#current];
      if (initialSlide) {
        this.#updateVisibleSlides();
        await this.#scroll.to(initialSlide, { instant: true });
      }

      this.#updateControlsVisibility();
    } catch (e) {
      console.error('[Slideshow] Init error', e);
    } finally {
      this.#isInitializing = false;
    }
  }

  #destroyScroller() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#scroll) {
      this.#scroll.destroy();
      this.#scroll = null;
    }
  }

  refsUpdated() {
    this.#ensureCurrentSlide();
    this.#updateControlsVisibility();
    this.#updateVisibleSlides();
  }

  async select(target, behavior, options) {
    const event = behavior instanceof Event ? behavior : options instanceof Event ? options : undefined;
    this.#consumeInteractionEvent(event);
    const scrollOptions = this.#normalizeScrollOptions(behavior, options);
    const index = this.#resolveTargetIndex(target);
    const slide = this.slides[index];

    if (!slide) return;
    const ready = await this.#ensureReady();
    if (!ready || !this.#scroll) return;

    this.toggleAttribute('actioned', true);
    this.#setCurrent(index, { trigger: 'select', userInitiated: Boolean(event?.isTrusted) });
    await this.#scroll.to(slide, scrollOptions);
    requestAnimationFrame(() => this.removeAttribute('actioned'));
  }

  next(event) {
    this.#consumeInteractionEvent(event);
    this.#step(1, event);
  }

  previous(event) {
    this.#consumeInteractionEvent(event);
    this.#step(-1, event);
  }

  play(interval = this.autoplayInterval) {
    if (!interval) return;

    this.paused = false;
    this.suspend();
    this.#autoplayTimer = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  suspend = () => {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused || document.hidden || this.#autoplayTimer) return;

    this.play();
  };

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #consumeInteractionEvent(event) {
    if (!(event instanceof Event)) return;

    event.preventDefault();
    event.stopPropagation();
  }

  async #ensureReady() {
    if (this.#scroll) return true;

    await this.#init();

    return Boolean(this.#scroll);
  }

  #normalizeScrollOptions(behavior, options) {
    const input =
      behavior instanceof Event
        ? {}
        : typeof behavior === 'string'
          ? { behavior }
          : behavior && typeof behavior === 'object'
            ? behavior
            : options && !(options instanceof Event)
              ? options
              : {};

    return {
      instant: input.animate === false || input.instant === true || input.behavior === 'instant',
    };
  }

  #resolveTargetIndex(target) {
    if (target instanceof HTMLElement) {
      return this.slides.indexOf(target);
    }

    if (typeof target === 'object' && target !== null && 'id' in target) {
      return this.slides.findIndex((slide) => slide.getAttribute('slide-id') === `${target.id}`);
    }

    const index = parseInt(`${target}`, 10);
    if (Number.isNaN(index)) return -1;

    return Math.max(0, Math.min(index, this.slides.length - 1));
  }

  #getInitialSlideIndex() {
    const attributeValue = parseInt(this.getAttribute('initial-slide') || '0', 10);
    const requestedIndex = Number.isNaN(attributeValue) ? 0 : Math.max(0, attributeValue);
    const requestedSlide = this.slides[requestedIndex];

    if (requestedSlide && !requestedSlide.hidden) return requestedIndex;

    return this.#getNavigableIndexes()[0] ?? requestedIndex;
  }

  #getNavigableIndexes() {
    return this.slides.reduce((indexes, slide, index) => {
      if (!slide.hidden) indexes.push(index);
      return indexes;
    }, []);
  }

  #step(direction, event) {
    const navigableIndexes = this.#getNavigableIndexes();
    if (navigableIndexes.length <= 1) return;

    const currentPosition = navigableIndexes.indexOf(this.#current);
    const fallbackPosition = direction > 0 ? 0 : navigableIndexes.length - 1;
    let nextPosition = currentPosition === -1 ? fallbackPosition : currentPosition + direction;

    if (this.infinite) {
      nextPosition = (nextPosition + navigableIndexes.length) % navigableIndexes.length;
    } else {
      nextPosition = Math.max(0, Math.min(nextPosition, navigableIndexes.length - 1));
      if (nextPosition === currentPosition) return;
    }

    void this.select(navigableIndexes[nextPosition], event);
  }

  #ensureCurrentSlide() {
    const currentSlide = this.slides[this.#current];
    if (currentSlide && !currentSlide.hidden) return;

    this.#current = this.#getNavigableIndexes()[0] ?? 0;
  }

  #setCurrent(index, { trigger = 'select', userInitiated = false } = {}) {
    const slide = this.slides[index];
    if (!slide) return;

    const previousIndex = this.#current;
    this.#current = index;
    this.#updateVisibleSlides();
    this.#updateControlsVisibility();
    this.#sync();

    if (previousIndex === index) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        id: slide.getAttribute('slide-id'),
        slide,
        previousIndex,
        userInitiated,
        trigger,
      })
    );
  }

  get slides() {
    return Array.from(this.refs.scroller?.children || []).filter((slide) => slide instanceof HTMLElement);
  }

  get visibleSlides() {
    const { scroller } = this.refs;
    if (!scroller) return [];
    const containerRect = scroller.getBoundingClientRect();
    return Array.from(this.slides).filter(slide => {
      const rect = slide.getBoundingClientRect();
      return rect.left >= containerRect.left && rect.right <= containerRect.right;
    });
  }

  #handleScroll = debounce(() => {
    if (!this.#scroll) return;
    const index = this.#getClosestVisibleSlideIndex();
    if (index === -1 || index === this.#current) return;

    this.#setCurrent(index, { trigger: 'scroll', userInitiated: true });
  }, 50);

  #sync() {
    const current = this.querySelector('[ref="current"]');
    if (current) {
      current.textContent = `${this.#current + 1}`;
    }

    const controls = this.querySelectorAll('[ref="dots[]"], [ref="thumbnails[]"]');
    controls.forEach((control, index) => {
      control.setAttribute('aria-selected', `${index === this.#current}`);
    });

    const selectedThumbnail = this.querySelectorAll('[ref="thumbnails[]"]')[this.#current];
    if (selectedThumbnail instanceof HTMLElement) {
      this.#centerSelectedThumbnail(selectedThumbnail);
    }
  }

  #centerSelectedThumbnail(thumbnail) {
    const behavior = 'smooth';
    thumbnail.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
  }

  #handleTouchStart() {}
  #onTransitionEnd() {
    this.#updateVisibleSlides();
  }
  #handleMouseDown() {}

  #updateControlsVisibility() {
    const previous = this.querySelector('[ref="previous"]');
    const next = this.querySelector('[ref="next"]');
    const navigableIndexes = this.#getNavigableIndexes();

    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    if (navigableIndexes.length <= 1) {
      previous.disabled = true;
      next.disabled = true;
      return;
    }

    if (this.infinite) {
      previous.disabled = false;
      next.disabled = false;
      return;
    }

    const currentPosition = navigableIndexes.indexOf(this.#current);
    previous.disabled = currentPosition <= 0;
    next.disabled = currentPosition === -1 || currentPosition >= navigableIndexes.length - 1;
  }

  #updateVisibleSlides() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== this.#current}`);
    });
  }

  #getClosestVisibleSlideIndex() {
    const { scroller } = this.refs;
    if (!scroller) return -1;

    const axis = this.#scroll?.axis || 'x';
    const scrollProperty = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const offsetProperty = axis === 'x' ? 'offsetLeft' : 'offsetTop';
    const scrollPosition = scroller[scrollProperty];
    let closestIndex = -1;
    let smallestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      if (slide.hidden) return;

      const distance = Math.abs(slide[offsetProperty] - scrollPosition);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}
